import React, { useState, useEffect } from 'react';
import { 
  Search,
  Filter,
  Network,
  HardDrive,
  ChevronDown,
  RotateCw,
  Server,
  Copy,
  AlertTriangle,
  Plus,
  Trash2,
  ArrowLeftRight,
  AlertCircle,
  CheckCircle,
  XCircle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';

const TabPanel = ({ children, value, index, ...other }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`nodeservice-tabpanel-${index}`}
    aria-labelledby={`nodeservice-tab-${index}`}
    {...other}
  >
    {value === index && <div className="pt-4">{children}</div>}
  </div>
);

export default function NodeServiceView({
  user,
  clusters = [],
  nodes = {},
  unassignedNodes = [],
  services = {},
  pods = {},
  activeCluster,
  setActiveCluster,
  fetchNodes,
  fetchUnassignedNodes,
  fetchServices,
  fetchPods,
  fetchApps,
  updateNodeStatus,
  assignNode,
  removeNode,
  transferNode
}) {
  // Extract stable user properties to prevent infinite loops
  const userId = user?.user?._id;
  const userToken = user?.token;

  const [selectedCluster, setSelectedCluster] = useState(activeCluster || '');
  const [searchTerm, setSearchTerm] = useState('');
  const [nodeStatusFilter, setNodeStatusFilter] = useState('all');
  const [nodeRoleFilter, setNodeRoleFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('nodes');
  const [selectedPod, setSelectedPod] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [transferMenuOpen, setTransferMenuOpen] = useState(false);
  const [selectedNodeForTransfer, setSelectedNodeForTransfer] = useState(null);

  // Initial data fetching when component mounts
  useEffect(() => {
    if (userId && userToken) {
      fetchNodes(userId, userToken);
      fetchUnassignedNodes(userId, userToken);
      fetchServices(userId, userToken);
      fetchPods(userId, userToken);
      fetchApps(userId, userToken);
    }
  }, [userId, userToken, fetchNodes, fetchUnassignedNodes, fetchServices, fetchPods, fetchApps]);

  // Sync with global active cluster
  useEffect(() => {
    if (activeCluster && activeCluster !== selectedCluster) {
      setSelectedCluster(activeCluster);
    }
  }, [activeCluster, selectedCluster]);

  // Fetch data when cluster changes
  useEffect(() => {
    if (selectedCluster && userId && userToken) {
      setIsLoading(true);
      Promise.all([
        fetchNodes(userId, userToken, selectedCluster),
        fetchUnassignedNodes(userId, userToken)
      ]).finally(() => setIsLoading(false));
    }
  }, [selectedCluster, userId, userToken, fetchNodes, fetchUnassignedNodes]);

  const handleClusterChange = (value) => {
    setSelectedCluster(value);
    if (setActiveCluster) {
      setActiveCluster(value);
    }
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleRefresh = () => {
    if (selectedCluster && userId && userToken) {
      setIsLoading(true);
      Promise.all([
        fetchNodes(userId, userToken, selectedCluster),
        fetchUnassignedNodes(userId, userToken)
      ]).finally(() => setIsLoading(false));
    }
  };
  
  const handleNodeStatusChange = (nodeId, checked) => {
    if (userId && userToken && selectedCluster) {
      updateNodeStatus(
        userId,
        userToken,
        selectedCluster,
        nodeId,
        checked ? 'Ready' : 'NotReady'
      );
    }
  };

  const handleAssignNode = (nodeId) => {
    if (userId && userToken && selectedCluster) {
      assignNode(userId, userToken, selectedCluster, nodeId);
    }
  };

  const handleRemoveNode = (nodeId) => {
    if (userId && userToken && selectedCluster) {
      removeNode(userId, userToken, selectedCluster, nodeId);
    }
  };

  const handleTransferNode = (targetClusterId) => {
    if (userId && userToken && selectedCluster && selectedNodeForTransfer) {
      transferNode(
        userId,
        userToken,
        selectedCluster,
        targetClusterId,
        selectedNodeForTransfer
      );
      setTransferMenuOpen(false);
      setSelectedNodeForTransfer(null);
    }
  };
  
  const handleNodeStatusFilterChange = (value) => {
    setNodeStatusFilter(value);
  };

  const handleNodeRoleFilterChange = (value) => {
    setNodeRoleFilter(value);
  };
  
  // Get data for the selected cluster
  const clusterNodes = nodes[selectedCluster] || [];
  const clusterServices = services[selectedCluster] || [];
  const clusterPods = pods[selectedCluster] || [];
  
  // Mock data for services and pods until Redux store has them
  const mockServices = [
    { id: 1, name: 'nginx-service', namespace: 'default', type: 'ClusterIP', clusterIP: '10.96.0.1', externalIP: 'None', ports: '80/TCP', age: '5d' },
    { id: 2, name: 'kubernetes', namespace: 'default', type: 'ClusterIP', clusterIP: '10.96.0.1', externalIP: 'None', ports: '443/TCP', age: '10d' },
    { id: 3, name: 'kube-dns', namespace: 'kube-system', type: 'ClusterIP', clusterIP: '10.96.0.10', externalIP: 'None', ports: '53/UDP,53/TCP', age: '10d' }
  ];

  const mockPods = [
    { 
      id: 1, 
      name: 'nginx-deployment-54f57cf6bf-9pnxh', 
      namespace: 'default', 
      status: 'Running', 
      restarts: 0, 
      age: '2d', 
      node: 'worker-1',
      logs: [
        '2024-01-15 10:30:15 [info] Starting nginx server',
        '2024-01-15 10:30:16 [info] Server listening on port 80',
        '2024-01-15 10:30:17 [info] Ready to accept connections'
      ]
    },
    { 
      id: 2, 
      name: 'coredns-558bd4d5db-x7gvn', 
      namespace: 'kube-system', 
      status: 'Running', 
      restarts: 0, 
      age: '10d', 
      node: 'master-1',
      logs: [
        '2024-01-05 08:15:30 [info] CoreDNS starting',
        '2024-01-05 08:15:31 [info] DNS server ready on port 53'
      ]
    }
  ];

  // Filter nodes based on search and filters
  const filteredNodes = clusterNodes.filter(
    node =>
      (node.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        node.ip?.includes(searchTerm) ||
        node.role.toLowerCase().includes(searchTerm.toLowerCase())) &&
      (nodeStatusFilter === 'all' || node.status.toLowerCase() === nodeStatusFilter) &&
      (nodeRoleFilter === 'all' || node.role.toLowerCase() === nodeRoleFilter)
  );

  const filteredServices = mockServices.filter(
    service => service.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
              service.namespace.toLowerCase().includes(searchTerm.toLowerCase()) ||
              service.type.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  const filteredPods = mockPods.filter(
    pod => pod.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          pod.namespace.toLowerCase().includes(searchTerm.toLowerCase()) ||
          pod.status.toLowerCase().includes(searchTerm.toLowerCase()) ||
          pod.node.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredUnassignedNodes = unassignedNodes.filter(
    node =>
      (node.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        node.ip.includes(searchTerm) ||
        node.role.toLowerCase().includes(searchTerm.toLowerCase())) &&
      (nodeStatusFilter === 'all' || node.status.toLowerCase() === nodeStatusFilter) &&
      (nodeRoleFilter === 'all' || node.role.toLowerCase() === nodeRoleFilter)
  );

  // Calculate totals for node resources
  const totalCores = clusterNodes.reduce((acc, node) => {
    const cores = parseInt(node.cpu?.split(' ')[0] || 0);
    return acc + cores;
  }, 0);

  return (
    <div className="flex flex-col space-y-4">
      {/* Header with cluster selection and controls */}
      <Card className="p-6 rounded-xl border border-gray-200">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-4">
          <h2 className="text-xl font-bold">Nodes & Services Management</h2>
          <div className="flex flex-col sm:flex-row gap-3">
            <Select value={selectedCluster} onValueChange={handleClusterChange}>
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue placeholder="Select Cluster" />
              </SelectTrigger>
              <SelectContent>
                {clusters.map(cluster => (
                  <SelectItem key={cluster.id} value={cluster.id}>
                    <div className="flex items-center gap-2">
                      <Server className="w-4 h-4 text-blue-600" />
                      {cluster.name}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button onClick={handleRefresh} disabled={isLoading} variant="outline">
              <RotateCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Search and Filter Controls */}
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search nodes, services, or pods..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="pl-10"
            />
          </div>
          <Select value={nodeStatusFilter} onValueChange={handleNodeStatusFilterChange}>
            <SelectTrigger className="w-full sm:w-40">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="ready">Ready</SelectItem>
              <SelectItem value="notready">Not Ready</SelectItem>
              <SelectItem value="running">Running</SelectItem>
            </SelectContent>
          </Select>
          <Select value={nodeRoleFilter} onValueChange={handleNodeRoleFilterChange}>
            <SelectTrigger className="w-full sm:w-40">
              <SelectValue placeholder="Role" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Roles</SelectItem>
              <SelectItem value="master">Master</SelectItem>
              <SelectItem value="worker">Worker</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Card>

      {/* Content Tabs */}
      <Card className="flex-1 rounded-xl border border-gray-200">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="h-full">
          <div className="border-b border-gray-200 px-6 pt-6">
            <TabsList className="grid w-full grid-cols-4 bg-gray-100">
              <TabsTrigger value="nodes" className="flex items-center gap-2">
                <Server className="w-4 h-4" />
                Cluster Nodes ({filteredNodes.length})
              </TabsTrigger>
              <TabsTrigger value="unassigned" className="flex items-center gap-2">
                <HardDrive className="w-4 h-4" />
                Unassigned ({filteredUnassignedNodes.length})
              </TabsTrigger>
              <TabsTrigger value="services" className="flex items-center gap-2">
                <Network className="w-4 h-4" />
                Services ({filteredServices.length})
              </TabsTrigger>
              <TabsTrigger value="pods" className="flex items-center gap-2">
                <Copy className="w-4 h-4" />
                Pods ({filteredPods.length})
              </TabsTrigger>
            </TabsList>
          </div>

          <div className="p-6">
            {/* Cluster Nodes Tab */}
            <TabsContent value="nodes" className="mt-0">
              {!selectedCluster ? (
                <Alert>
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>
                    Please select a cluster to view its nodes.
                  </AlertDescription>
                </Alert>
              ) : filteredNodes.length === 0 ? (
                <div className="text-center py-12">
                  <Server className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium mb-2">No Nodes Found</h3>
                  <p className="text-gray-600 mb-4">
                    {clusterNodes.length === 0 
                      ? "This cluster doesn't have any nodes assigned yet."
                      : "No nodes match your current search and filter criteria."
                    }
                  </p>
                  <Button variant="outline" onClick={() => setSearchTerm('')}>
                    Clear Filters
                  </Button>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Node Name</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Role</TableHead>
                        <TableHead>IP Address</TableHead>
                        <TableHead>CPU</TableHead>
                        <TableHead>Memory</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredNodes.map((node) => (
                        <TableRow key={node.id}>
                          <TableCell className="font-medium">{node.name}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Switch 
                                checked={node.status === 'Ready' || node.status === 'running'}
                                onCheckedChange={(checked) => handleNodeStatusChange(node.id, checked)}
                              />
                              <Badge variant={
                                node.status === 'Ready' || node.status === 'running' 
                                  ? 'default' 
                                  : 'destructive'
                              }>
                                {node.status}
                              </Badge>
                            </div>
                          </TableCell>
                          <TableCell>{node.role}</TableCell>
                          <TableCell className="font-mono text-sm">{node.ip}</TableCell>
                          <TableCell>{node.cpu || 'N/A'}</TableCell>
                          <TableCell>{node.memory || 'N/A'}</TableCell>
                          <TableCell>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="outline" size="sm">
                                  <ChevronDown className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent>
                                <DropdownMenuItem onClick={() => handleRemoveNode(node.id)}>
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Remove from Cluster
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                  onClick={() => {
                                    setSelectedNodeForTransfer(node.id);
                                    setTransferMenuOpen(true);
                                  }}
                                >
                                  <ArrowLeftRight className="w-4 h-4 mr-2" />
                                  Transfer to Cluster
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </TabsContent>

            {/* Unassigned Nodes Tab */}
            <TabsContent value="unassigned" className="mt-0">
              {filteredUnassignedNodes.length === 0 ? (
                <div className="text-center py-12">
                  <HardDrive className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium mb-2">No Unassigned Nodes</h3>
                  <p className="text-gray-600 mb-4">
                    All registered nodes are currently assigned to clusters.
                  </p>
                  <Button variant="outline">
                    <Plus className="w-4 h-4 mr-2" />
                    Register New Device
                  </Button>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Node Name</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Role</TableHead>
                        <TableHead>IP Address</TableHead>
                        <TableHead>CPU</TableHead>
                        <TableHead>Memory</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredUnassignedNodes.map((node) => (
                        <TableRow key={node.id}>
                          <TableCell className="font-medium">{node.name}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{node.status}</Badge>
                          </TableCell>
                          <TableCell>{node.role}</TableCell>
                          <TableCell className="font-mono text-sm">{node.ip}</TableCell>
                          <TableCell>{node.cpu || 'N/A'}</TableCell>
                          <TableCell>{node.memory || 'N/A'}</TableCell>
                          <TableCell>
                            <Button 
                              size="sm" 
                              onClick={() => handleAssignNode(node.id)}
                              disabled={!selectedCluster}
                            >
                              <Plus className="w-4 h-4 mr-2" />
                              Assign to {selectedCluster ? 'Cluster' : 'Select Cluster'}
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </TabsContent>

            {/* Services Tab */}
            <TabsContent value="services" className="mt-0">
              {!selectedCluster ? (
                <Alert>
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>
                    Please select a cluster to view its services.
                  </AlertDescription>
                </Alert>
              ) : filteredServices.length === 0 ? (
                <div className="text-center py-12">
                  <Network className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium mb-2">No Services Found</h3>
                  <p className="text-gray-600">No services match your search criteria.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Service Name</TableHead>
                        <TableHead>Namespace</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Cluster IP</TableHead>
                        <TableHead>External IP</TableHead>
                        <TableHead>Ports</TableHead>
                        <TableHead>Age</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredServices.map((service) => (
                        <TableRow key={service.id}>
                          <TableCell className="font-medium">{service.name}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{service.namespace}</Badge>
                          </TableCell>
                          <TableCell>{service.type}</TableCell>
                          <TableCell className="font-mono text-sm">{service.clusterIP}</TableCell>
                          <TableCell className="font-mono text-sm">{service.externalIP}</TableCell>
                          <TableCell className="font-mono text-sm">{service.ports}</TableCell>
                          <TableCell>{service.age}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </TabsContent>

            {/* Pods Tab */}
            <TabsContent value="pods" className="mt-0">
              {!selectedCluster ? (
                <Alert>
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>
                    Please select a cluster to view its pods.
                  </AlertDescription>
                </Alert>
              ) : filteredPods.length === 0 ? (
                <div className="text-center py-12">
                  <Copy className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium mb-2">No Pods Found</h3>
                  <p className="text-gray-600">No pods match your search criteria.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Pod Name</TableHead>
                        <TableHead>Namespace</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Restarts</TableHead>
                        <TableHead>Node</TableHead>
                        <TableHead>Age</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredPods.map((pod) => (
                        <TableRow key={pod.id}>
                          <TableCell className="font-medium">{pod.name}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{pod.namespace}</Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {pod.status === 'Running' ? (
                                <CheckCircle className="w-4 h-4 text-green-600" />
                              ) : (
                                <XCircle className="w-4 h-4 text-red-600" />
                              )}
                              <Badge variant={pod.status === 'Running' ? 'default' : 'destructive'}>
                                {pod.status}
                              </Badge>
                            </div>
                          </TableCell>
                          <TableCell>{pod.restarts}</TableCell>
                          <TableCell>{pod.node}</TableCell>
                          <TableCell>{pod.age}</TableCell>
                          <TableCell>
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => setSelectedPod(pod)}
                            >
                              View Logs
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </TabsContent>
          </div>
        </Tabs>
      </Card>

      {/* Pod Logs Dialog */}
      <Dialog open={!!selectedPod} onOpenChange={() => setSelectedPod(null)}>
        <DialogContent className="max-w-4xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>Pod Logs: {selectedPod?.name}</DialogTitle>
            <DialogDescription>
              Recent logs from {selectedPod?.namespace} namespace
            </DialogDescription>
          </DialogHeader>
          <div className="bg-black text-green-400 p-4 rounded font-mono text-sm max-h-96 overflow-auto">
            {selectedPod?.logs?.map((log, index) => (
              <div key={index} className="mb-1">{log}</div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedPod(null)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Transfer Node Dialog */}
      <Dialog open={transferMenuOpen} onOpenChange={setTransferMenuOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Transfer Node to Another Cluster</DialogTitle>
            <DialogDescription>
              Select the target cluster for node transfer.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Select onValueChange={handleTransferNode}>
              <SelectTrigger>
                <SelectValue placeholder="Select target cluster" />
              </SelectTrigger>
              <SelectContent>
                {clusters
                  .filter(cluster => cluster.id !== selectedCluster)
                  .map(cluster => (
                    <SelectItem key={cluster.id} value={cluster.id}>
                      {cluster.name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTransferMenuOpen(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
} 